'use client';
import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import {
  BellIcon,
  XMarkIcon,
  CheckIcon,
  TrashIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  CheckCircleIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';
import { Notification } from '@/hooks/useNotifications';

interface NotificationPanelProps {
  open: boolean;
  onClose: () => void;
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  markAsRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  clearAll: () => Promise<void>;
}

const SEVERITY_CONFIG: Record<
  string,
  { icon: React.ElementType; bg: string; text: string; dot: string }
> = {
  info: {
    icon: InformationCircleIcon,
    bg: 'bg-blue-50 dark:bg-blue-950/30',
    text: 'text-blue-600',
    dot: 'bg-blue-500',
  },
  warning: {
    icon: ExclamationTriangleIcon,
    bg: 'bg-amber-50 dark:bg-amber-950/30',
    text: 'text-amber-600',
    dot: 'bg-amber-500',
  },
  error: {
    icon: XCircleIcon,
    bg: 'bg-red-50 dark:bg-red-950/30',
    text: 'text-red-600',
    dot: 'bg-red-500',
  },
  success: {
    icon: CheckCircleIcon,
    bg: 'bg-emerald-50 dark:bg-emerald-950/30',
    text: 'text-emerald-600',
    dot: 'bg-emerald-500',
  },
};

const EVENT_TYPE_LABELS: Record<string, string> = {
  fee_due: 'Fee Due',
  overdue_fees: 'Fee Overdue',
  grade_published: 'Grade Posted',
  application_status: 'Application Update',
  schedule_change: 'Schedule Update',
  message_received: 'New Message',
  course_scheduled: 'Timetable Update',
  system: 'System',
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString('en-PG', { day: 'numeric', month: 'short' });
}

export default function NotificationPanel({
  open,
  onClose,
  notifications,
  unreadCount,
  loading,
  markAsRead,
  markAllRead,
  clearAll,
}: NotificationPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open, onClose]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/20 z-40 transition-opacity duration-200 ${
          open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        aria-hidden="true"
      />

      {/* Slide-out panel */}
      <div
        ref={panelRef}
        role="dialog"
        aria-label="Notifications"
        aria-modal="true"
        className={`
          fixed top-0 right-0 h-full w-full sm:w-[400px] bg-card border-l border-border z-50
          flex flex-col shadow-2xl
          transition-transform duration-300 ease-in-out
          ${open ? 'translate-x-0' : 'translate-x-full'}
        `}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <BellIcon className="w-4 h-4 text-primary" />
            </div>
            <div>
              <h2 className="font-700 text-base text-foreground leading-tight">Notifications</h2>
              {unreadCount > 0 && (
                <p className="text-xs text-muted-foreground leading-tight">
                  {unreadCount} unread
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1">
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                title="Mark all as read"
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-500 text-primary hover:bg-primary/10 transition-colors"
              >
                <CheckIcon className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Mark all read</span>
              </button>
            )}
            {notifications.length > 0 && (
              <button
                onClick={clearAll}
                title="Clear all notifications"
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-500 text-muted-foreground hover:bg-muted hover:text-negative transition-colors"
              >
                <TrashIcon className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Clear all</span>
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-muted transition-colors ml-1"
              aria-label="Close notifications"
            >
              <XMarkIcon className="w-5 h-5 text-muted-foreground" />
            </button>
          </div>
        </div>

        {/* Filter tabs */}
        <div className="flex items-center gap-1 px-5 py-2.5 border-b border-border flex-shrink-0">
          <span className="text-xs font-600 text-foreground px-2 py-1 rounded-md bg-muted">
            All
          </span>
          {unreadCount > 0 && (
            <span className="text-xs text-muted-foreground px-2 py-1">
              {unreadCount} unread
            </span>
          )}
        </div>

        {/* Notification list */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex flex-col gap-3 p-5">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex gap-3 animate-pulse">
                  <div className="w-9 h-9 rounded-lg bg-muted flex-shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3.5 bg-muted rounded w-3/4" />
                    <div className="h-3 bg-muted rounded w-full" />
                    <div className="h-3 bg-muted rounded w-1/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-4 px-6 py-12">
              <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center">
                <BellIcon className="w-8 h-8 text-muted-foreground" />
              </div>
              <div className="text-center">
                <p className="font-600 text-foreground text-sm">All caught up!</p>
                <p className="text-muted-foreground text-xs mt-1">
                  No notifications to show right now.
                </p>
              </div>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {notifications.map((n) => {
                const cfg = SEVERITY_CONFIG[n.severity] || SEVERITY_CONFIG.info;
                const SeverityIcon = cfg.icon;
                const eventLabel = EVENT_TYPE_LABELS[n.eventType] || n.eventType;

                return (
                  <div
                    key={n.id}
                    onClick={() => !n.isRead && markAsRead(n.id)}
                    className={`
                      relative flex gap-3 px-5 py-4 cursor-pointer
                      hover:bg-muted/40 transition-colors group
                      ${!n.isRead ? 'bg-secondary/20' : ''}
                    `}
                  >
                    {/* Unread indicator */}
                    {!n.isRead && (
                      <span
                        className={`absolute left-2 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full ${cfg.dot}`}
                      />
                    )}

                    {/* Icon */}
                    <div
                      className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${cfg.bg}`}
                    >
                      <SeverityIcon className={`w-4.5 h-4.5 ${cfg.text}`} />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <span className="inline-block text-[10px] font-600 uppercase tracking-wide text-muted-foreground mb-0.5">
                            {eventLabel}
                          </span>
                          <p
                            className={`text-sm leading-snug ${
                              !n.isRead
                                ? 'text-foreground font-600'
                                : 'text-muted-foreground font-400'
                            }`}
                          >
                            {n.title}
                          </p>
                        </div>
                        <span className="text-[10px] text-muted-foreground flex-shrink-0 mt-0.5">
                          {timeAgo(n.createdAt)}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 leading-relaxed line-clamp-2">
                        {n.body}
                      </p>
                      {n.link && (
                        <Link
                          href={n.link}
                          onClick={(e) => {
                            e.stopPropagation();
                            onClose();
                          }}
                          className="inline-flex items-center gap-1 text-xs text-primary font-500 mt-1.5 hover:underline"
                        >
                          View details
                          <ArrowTopRightOnSquareIcon className="w-3 h-3" />
                        </Link>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-border px-5 py-3 flex-shrink-0 flex items-center justify-between">
          <Link
            href="/notifications"
            onClick={onClose}
            className="flex items-center gap-1.5 text-sm font-500 text-primary hover:underline"
          >
            View all notifications
            <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
          </Link>
          <Link
            href="/notification-rules"
            onClick={onClose}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            Preferences
          </Link>
        </div>
      </div>
    </>
  );
}
